// Owner self-onboarding parser — wire DTO → view model. The seam every consumer
// respects: containers and components never read a DTO field directly.

import type {
  OwnerBankAccountDto,
  OwnerKycSessionDto,
  OwnerKycStatusDto,
  OwnerOnboardingCountryDto,
  OwnerOnboardingContextDto,
  OwnerOnboardingSessionDto,
  OwnerOnboardingStepDto,
  SellerOwnerOnboardingAvailabilityDto,
  SellerOwnerOnboardingSessionDto,
} from './ownerOnboardingContracts'
import type {
  OwnerBankAccount,
  OwnerKycSession,
  OwnerKycState,
  OwnerOnboardingCountry,
  OwnerOnboardingContext,
  OwnerOnboardingSession,
  OwnerOnboardingStep,
  SellerOwnerOnboardingAvailability,
  SellerOwnerOnboardingSessionGrant,
} from '../types/ownerOnboardingTypes'

function parseStep(dto: OwnerOnboardingStepDto): OwnerOnboardingStep {
  return {
    flowStepId: String(dto.flowStepId ?? ''),
    stepOrder: Number(dto.stepOrder ?? 0),
    stepKey: String(dto.stepKey ?? ''),
    stepLabel: String(dto.stepLabel ?? ''),
    description: dto.description ?? null,
    isMandatory: Boolean(dto.isMandatory),
    status: String(dto.status ?? ''),
  }
}

export function parseOwnerOnboardingContext(
  dto: OwnerOnboardingContextDto,
): OwnerOnboardingContext {
  return {
    firstName: String(dto.firstName ?? ''),
    maskedEmail: String(dto.maskedEmail ?? ''),
    requestRefCode: dto.requestRefCode ?? null,
    propertyName: dto.propertyName ?? null,
    inviteExpiresAt: String(dto.inviteExpiresAt ?? ''),
    // Sorted here rather than trusted from the wire: the wizard's rail renders in
    // array order, and one out-of-order row would misstate the owner's progress.
    steps: [...(dto.steps ?? [])].map(parseStep).sort((a, b) => a.stepOrder - b.stepOrder),
    currentStepKey: dto.currentStepKey ?? null,
    isComplete: Boolean(dto.isComplete),
    isSubmitted: Boolean(dto.isSubmitted),
    isEmailOtpFallbackEnabled: Boolean(dto.isEmailOtpFallbackEnabled),
  }
}

export function parseOwnerOnboardingSession(
  dto: OwnerOnboardingSessionDto,
): OwnerOnboardingSession {
  return {
    sessionToken: String(dto.sessionToken ?? ''),
    expiresInSeconds: Number(dto.expiresInSeconds ?? 0),
    context: parseOwnerOnboardingContext(dto.context),
  }
}

export function parseSellerOwnerOnboardingAvailability(
  dto: SellerOwnerOnboardingAvailabilityDto,
): SellerOwnerOnboardingAvailability {
  const ownerIndex = typeof dto.ownerIndex === 'number' ? dto.ownerIndex : null
  return { isAvailable: dto.isAvailable === true && ownerIndex !== null, ownerIndex }
}

export function parseSellerOwnerOnboardingSession(
  dto: SellerOwnerOnboardingSessionDto,
): SellerOwnerOnboardingSessionGrant {
  return {
    sessionToken: String(dto.sessionToken ?? ''),
    expiresInSeconds: Number(dto.expiresInSeconds ?? 0),
  }
}

export function parseOwnerOnboardingCountries(
  dtos: OwnerOnboardingCountryDto[],
): OwnerOnboardingCountry[] {
  return (
    dtos
      .map((dto) => ({
        id: String(dto.id ?? ''),
        isoCode2: String(dto.isoCode2 ?? ''),
        countryName: String(dto.countryName ?? ''),
      }))
      // Sorted here so every picker in the wizard reads the same way without each
      // form re-sorting it.
      .sort((a, b) => a.countryName.localeCompare(b.countryName))
  )
}

export function parseOwnerKycSession(dto: OwnerKycSessionDto): OwnerKycSession {
  return {
    accessToken: String(dto.accessToken ?? ''),
    levelName: String(dto.levelName ?? ''),
    kycStatus: String(dto.kycStatus ?? ''),
  }
}

export function parseOwnerKycStatus(dto: OwnerKycStatusDto): OwnerKycState {
  return {
    kycStatus: String(dto.kycStatus ?? ''),
    isRejected: Boolean(dto.isRejected),
    canRetry: Boolean(dto.canRetry),
    statusMessage: dto.statusMessage ?? '',
    attemptsUsed: Number(dto.attemptsUsed ?? 0),
    maxAttempts: Number(dto.maxAttempts ?? 0),
  }
}

export function parseOwnerBankAccount(dto: OwnerBankAccountDto): OwnerBankAccount {
  return {
    id: String(dto.id ?? ''),
    accountHolderName: String(dto.accountHolderName ?? ''),
    bankName: String(dto.bankName ?? ''),
    currency: String(dto.currency ?? ''),
    maskedAccountNumber: String(dto.maskedAccountNumber ?? ''),
    maskedIban: dto.maskedIban ?? null,
    ifscCode: dto.ifscCode ?? null,
    bankAccountStatus: String(dto.bankAccountStatus ?? ''),
  }
}
